// Bug report modal — mirrors vanilla openBugReportModal() (public/app/index.html L20551).
// Writes to shared/bugreport__<ts> per firestore.rules schema (type/description/sentAt required).

import { useEffect, useState } from 'react'
import { setShared } from '../lib/db/shared'
import { useAuth } from '../hooks/useAuth'
import { useOpts } from '../hooks/useOpts'

type ReportType = 'bug' | 'feature' | 'feedback'

interface TypeOption {
  key: ReportType
  icon: string
  label: string
  desc: string
}

const TYPES: TypeOption[] = [
  { key: 'bug', icon: '🐛', label: 'Bug Report', desc: 'Something is broken or not working right' },
  { key: 'feature', icon: '💡', label: 'Feature Request', desc: "An idea or improvement you'd like to see" },
  { key: 'feedback', icon: '💬', label: 'General Feedback', desc: 'Anything else on your mind' },
]

interface Props {
  open: boolean
  onClose: () => void
}

export default function BugReportModal({ open, onClose }: Props) {
  const { user } = useAuth()
  const opts = useOpts()
  const [type, setType] = useState<ReportType>('bug')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (open) {
      setType('bug')
      setSubject('')
      setDescription('')
      setErr(null)
    }
  }, [open])

  if (!open) return null

  async function handleSubmit() {
    const desc = description.trim()
    if (!desc) { setErr('Please describe the issue'); return }
    setBusy(true)
    setErr(null)
    try {
      const id = `bugreport__${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      await setShared(id, {
        id,
        type,
        subject: subject.trim(),
        description: desc,
        fromEmail: (user?.email ?? '').toLowerCase(),
        fromName: (opts.bizName as string | undefined) || (opts.teamCode as string | undefined) || '',
        fromTeam: ((opts.teamCode as string | undefined) ?? '').toUpperCase(),
        sentAt: new Date().toISOString(),
        status: 'new',
        ua: navigator.userAgent,
        appVersion: document.title || 'JobSite Pulse',
        url: location.href,
      })
      onClose()
    } catch (e) {
      setErr('Failed to send — check your connection')
      setBusy(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>Report an Issue</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              We read every report. Thank you for helping improve JobSite Pulse.
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ border: 'none', background: 'transparent', color: 'var(--text-faint)', fontSize: 18, cursor: 'pointer', padding: '2px 8px' }}>
            ✕
          </button>
        </div>

        <div className="flbl">Type</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
          {TYPES.map((t) => (
            <button key={t.key} type="button" onClick={() => setType(t.key)}
              style={{
                padding: '12px 8px',
                background: type === t.key ? 'var(--accent-light)' : 'var(--surface-alt)',
                border: `1px solid ${type === t.key ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                color: 'var(--text)',
                transition: 'all 0.15s',
              }}
              title={t.desc}
            >
              <span style={{ fontSize: 22 }}>{t.icon}</span>
              <span style={{ fontSize: 11, fontWeight: 700 }}>{t.label}</span>
            </button>
          ))}
        </div>

        <div className="flbl">Subject (optional)</div>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Short summary of the issue"
          maxLength={200}
          style={{ marginBottom: 12 }}
        />

        <div className="flbl"><span>Description</span> <span style={{ color: 'var(--red)' }}>*</span></div>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe what happened, what you expected, and any steps to reproduce…"
          rows={5}
          maxLength={4800}
          style={{ resize: 'vertical', minHeight: 100 }}
        />

        {err && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--red)' }}>{err}</div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={busy}>
            {busy ? 'Sending…' : 'Send Report'}
          </button>
        </div>
      </div>
    </div>
  )
}
